import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Tiny error boundary for chat surfaces. If anything throws inside a message
 * render (LLM produces unexpected payload, sticker mapping breaks, etc.) we
 * show a calm fallback card instead of crashing the entire panel.
 *
 * Usage:
 *   <ChatErrorBoundary>
 *     <MessageList ... />
 *   </ChatErrorBoundary>
 */
export default class ChatErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console for devs; never re-throw.
    console.warn("ChatErrorBoundary caught render error:", error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="mx-3 my-3 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900 shadow-sm"
          data-testid="chat-error-fallback"
          role="alert"
        >
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="flex-1">
            <p className="font-semibold">Couldn't render this message</p>
            <p className="mt-0.5 text-xs leading-5 text-amber-800/80">
              Something went wrong while displaying the conversation. Refresh
              the chat to continue — your profile is safe.
            </p>
            <button
              type="button"
              onClick={this.handleRetry}
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-600 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-700"
              data-testid="chat-error-retry-btn"
            >
              <RefreshCw size={12} /> Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
